import { Trans } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { StripeElements } from '@stripe/stripe-js';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Chip,
  Grid,
  List,
  Alert,
  Stack,
  Button,
  ListItem,
  useTheme,
  Container,
  Typography,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import successAnimation from 'src/assets/lottie/success.json';
import { CompanySizeRange } from 'src/services/organisation/organisation.types';
import { authApi, useGetAuthContextQuery } from 'src/services/auth/auth.service';
import { Product, SubscriptionStatus } from 'src/services/billing/billing.types';
import { useGetCompanyProfileQuery } from 'src/services/organisation/organisation.service';
import { getProductPrice, getProductTiers, getIntentErrorMessage, getSubscriptionErrorMessage, getConfirmPaymentErrorMessage } from 'src/services/billing/billing.utils';
import {
  useGetProductsQuery,
  useGetMySubscriptionQuery,
  useGetMyPaymentMethodsQuery,
  useCreatePaymentMethodMutation,
  useActivateSubscriptionMutation,
  useInitiatePaymentMethodSetupMutation,
} from 'src/services/billing/billing.service';

import Iconify from 'src/components/iconify';
import AnimationContainer from 'src/components/animation/container';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { PayPeriod, ClientPlans, RecruiterPlans } from 'src/types/payment';

type FeatureDesc = {
  identifier: string;
  available: boolean;
}

export default function SetupBillingView() {

  const { t } = useTranslate();

  const theme = useTheme();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const [planQuantity, setPlanQuantity] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<Product | null>(null);
  const [period, setPeriod] = useState<PayPeriod>(PayPeriod.Monthly);
  const [initialContext, setInitialContext] = useState(true);

  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [view, setView] = useState<'plans' | 'payment' | 'pending' | 'success' | 'incomplete_pending'>('plans');

  // Payment related states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripe = useStripe();
  const elements = useElements() as StripeElements;

  const { data: authContext, refetch } = useGetAuthContextQuery();

  const [activateSubscription, { isLoading: isActivating }] = useActivateSubscriptionMutation();
  const [getSetupIntent] = useInitiatePaymentMethodSetupMutation();
  const [createPaymentMethod] = useCreatePaymentMethodMutation();

  const { data: companyProfile, isLoading: isLoadingCompanyProfile } = useGetCompanyProfileQuery(
    authContext?.organization.id as string, {
    skip: !authContext?.organization.id
  });

  const { currentData: paymentMethods, isLoading: isPaymentMethodsLoading } = useGetMyPaymentMethodsQuery({
    page: 1,
    per_page: 4,
  });

  const { data: subscription, isLoading: isSubscriptionLoading, refetch: refetchSub } = useGetMySubscriptionQuery();

  const { data: availableProducts, isLoading: isAvailableProductsLoading } = useGetProductsQuery();

  useEffect(() => {

    if (!subscription || !authContext || !initialContext) {
      return;
    }

    // check if valid tenant type
    if ([TenantType.Candidate].includes(authContext.organization.type)) {
      // send candidates to the dashboard
      router.push(paths.dashboard.root)
    }

    // check if user has existing subscription
    if (subscription) {
      // if tenant has a subscription, redirect to the dashboard
      router.push(paths.dashboard.root)
    }

    setView('plans');

  }, [router, subscription, authContext, initialContext]);

  const premiumPlan = useMemo(() => {
    if (!availableProducts || !authContext || !companyProfile) {
      return null;
    }

    // find the premium plan based on the tenant type
    const premiumProducts = availableProducts.filter(p => p.group === 'premium');

    const tenantType = authContext.organization.type;
    let planIdentifier: ClientPlans | RecruiterPlans | null = null;
    let product: Product | undefined;

    // if quantity is less than the product's maximum quantity, set the plan identifier based on the company size
    // otherwise, set the plan identifier based the quantity

    if (tenantType === TenantType.Recruiter) {
      // one per-user plan whatever the agency's size (JT1-1835)
      planIdentifier = RecruiterPlans.AgencyPremium;
    } else if (tenantType === TenantType.Client) {
      switch (companyProfile?.company_size) {
        case CompanySizeRange.LESS_THAN_50:
          planIdentifier = ClientPlans.BusinessStartup;
          break;
        case CompanySizeRange.FIFTY_ONE_TO_100:
          planIdentifier = ClientPlans.BusinessSmall;
          break;
        case CompanySizeRange.ONE_HUNDRED_AND_ONE_TO_TWO_HUNDRED:
          planIdentifier = ClientPlans.BusinessMedium;
          break;
        case CompanySizeRange.TWO_HUNDRED_AND_ONE_TO_FIVE_HUNDRED:
          planIdentifier = ClientPlans.BusinessLarge;
          break;
        case CompanySizeRange.FIVE_HUNDRED_AND_ONE_AND_UP:
          planIdentifier = ClientPlans.BusinessXL;
          break;
        default:
          planIdentifier = ClientPlans.BusinessXL;
          break;
      }

      product = premiumProducts.find(p => p.identifier === planIdentifier);

      if (product?.max_quantity) {
        if (planQuantity > product.max_quantity) {
          // find the product that matches the plan quantity
          // there could be multiple products with different max quantities, need to find the closest one that is greater than or equal to the plan quantity
          const matchingProduct = findClosestPlan(premiumProducts, planQuantity);

          if (matchingProduct) {
            planIdentifier = matchingProduct.identifier as ClientPlans;
          } else {
            planIdentifier = ClientPlans.BusinessXL; // fallback to the largest plan if no match found
          }
        }
      }
    }

    // find the product that matches the plan identifier
    return premiumProducts.find(p => p.identifier === planIdentifier);

  }, [availableProducts, authContext, companyProfile, planQuantity]);

  function findClosestPlan(plans: Product[], quantity: number): Product | null {

    // filter plans where max_quantity is greater than quantity or is null (infinite)
    const validPlans = plans.filter(plan => plan.max_quantity === null || plan.max_quantity > quantity);

    // gf no plans meet the criteria, return null
    if (validPlans.length === 0) return null;

    // find the plan with the smallest max_quantity (null is considered largest)
    return validPlans.reduce((closest: Product | null, plan: Product) => {

      // ff closest is null, it's infinite, so keep it unless plan is also null
      if (closest === null) return plan

      // ff plan.max_quantity is null, it's infinite, so keep closest
      if (plan.max_quantity === null) return closest;

      // ff closest.max_quantity is null, it's infinite, so keep plan
      if (closest.max_quantity === null) return plan;

      // otherwise, compare max_quantity values
      return plan.max_quantity < closest.max_quantity ? plan : closest;
    }, null);
  }

  const basePlan = useMemo(() => {
    if (!availableProducts || !authContext || !companyProfile) {
      return null;
    }

    // Find the premium plan based on the tenant type
    const tenantType = authContext.organization.type;
    let planIdentifier: ClientPlans | RecruiterPlans | null = null;

    if (tenantType === TenantType.Recruiter) {
      planIdentifier = RecruiterPlans.Basic;
    } else if (tenantType === TenantType.Client) {
      planIdentifier = ClientPlans.Basic;
    }

    // find the product that matches the plan identifier
    return availableProducts.find(p => p.identifier === planIdentifier);
  }, [availableProducts, authContext, companyProfile]);

  const planFeatures: { basic: FeatureDesc[], premium: FeatureDesc[] } = useMemo(() => {

    if (authContext?.organization.type === TenantType.Recruiter) {
      return {
        basic: [
          {
            identifier: 'job_posting',
            available: true,
          },
          {
            identifier: 'user_included',
            available: true,
          },
          {
            identifier: 'crm_ats',
            available: true,
          },
          {
            identifier: 'connect_candidates_clients',
            available: true,
          },
          {
            identifier: 'live_candidate_availability',
            available: true,
          },
          {
            identifier: 'unlimited_job_management',
            available: true,
          },
          {
            identifier: 'unlimited_reference_checks',
            available: true,
          },
          {
            identifier: 'built_in_interview_scheduling',
            available: true,
          },
          {
            identifier: 'jobtetris_themed_job_board',
            available: true,
          },
          {
            identifier: 'ai_tools',
            available: false,
          },
          {
            identifier: 'custom_branding',
            available: false,
          },
          {
            identifier: 'templates',
            available: false,
          },
          {
            identifier: 'email_sync',
            available: false,
          },
          {
            identifier: 'integrations',
            available: false,
          }
        ],
        premium: [
          {
            identifier: 'job_posting',
            available: true,
          },
          {
            identifier: 'ai_optimization_tools',
            available: true,
          },
          {
            identifier: 'unlimited_users',
            available: true,
          },
          {
            identifier: 'team_structures_permissions',
            available: true,
          },
          {
            identifier: 'customizable_job_board',
            available: true,
          },
          {
            identifier: 'sales_pipeline_deal_tracker',
            available: true,
          },
          {
            identifier: 'tasks_manager',
            available: true,
          },
          {
            identifier: 'lists',
            available: true,
          },
          {
            identifier: 'templates',
            available: true,
          },
          {
            identifier: 'email_sync',
            available: true,
          },
          {
            identifier: 'integrations',
            available: true,
          },
          {
            identifier: 'reports',
            available: true,
          }
        ]
      }
    }
    if (authContext?.organization.type === TenantType.Client) {
      return {
        basic: [
          {
            identifier: 'job_posting',
            available: true,
          },
          {
            identifier: 'user_included',
            available: true,
          },
          {
            identifier: 'crm_ats',
            available: true,
          },
          {
            identifier: 'connect_candidates_clients',
            available: true,
          },
          {
            identifier: 'unlimited_job_management',
            available: true,
          },
          {
            identifier: 'unlimited_reference_checks',
            available: true,
          },
          {
            identifier: 'built_in_interview_scheduling',
            available: true,
          },
          {
            identifier: 'jobtetris_themed_job_board',
            available: true,
          },
          {
            identifier: 'ai_tools',
            available: false,
          },
          {
            identifier: 'custom_branding',
            available: false,
          },
          {
            identifier: 'templates',
            available: false,
          },
          {
            identifier: 'email_sync',
            available: false,
          },
          {
            identifier: 'integrations',
            available: false,
          }
        ],
        premium: [
          {
            identifier: 'job_posting',
            available: true,
          },
          {
            identifier: 'ai_optimization_tools',
            available: true,
          },
          {
            identifier: 'unlimited_users',
            available: true,
          },
          {
            identifier: 'team_structures_permissions',
            available: true,
          },
          {
            identifier: 'customizable_job_board',
            available: true,
          },
          {
            identifier: 'tasks_manager',
            available: true,
          },
          {
            identifier: 'lists',
            available: true,
          },
          {
            identifier: 'templates',
            available: true,
          },
          {
            identifier: 'email_sync',
            available: true,
          },
          {
            identifier: 'integrations',
            available: true,
          },
          {
            identifier: 'reports',
            available: true,
          }
        ]
      }
    }

    return {
      basic: [],
      premium: []
    };

  }, [authContext]);

  const getIncludedTeamSeats = useCallback((product: Product) => {

    const teamSeatFeature = product.features?.find(
      (feature) => feature.type === FeatureType.TEAM_SEAT_LIMIT
    );

    if (teamSeatFeature) {
      return teamSeatFeature.value as number;
    }

    return 1;
  }, []);

  const switchProduct = useCallback((product: Product) => {
    if (!product) return;

    if (product.identifier === selectedPlan?.identifier) {
      // if the product is already selected, do nothing
      return;
    }

    if (selectedPlan && product.group !== selectedPlan.group) {
      setPlanQuantity(1);
    }

    setSelectedPlan(product);

  }, [selectedPlan]);

  const getPlanPrice = useCallback((plan: Product, periodType: PayPeriod) => {
    let cost = 0;
    const baseCost = getProductPrice(plan, periodType) as number;

    cost = baseCost;
    let seatCost = 0;
    if (plan.quantity_required) {
      // get the price tier for the selected quantity
      const priceTiers = getProductTiers(plan, periodType);
      if (priceTiers && priceTiers.length > 0) {
        const tier = priceTiers.find(
          (priceTier) =>
            priceTier.min_quantity <= planQuantity &&
            (priceTier.max_quantity >= planQuantity || priceTier.max_quantity === null)
        );

        if (tier) {
          seatCost = tier.amount;
        }
      }
      cost = baseCost + (seatCost * planQuantity);
    }

    return cost;
  }, [planQuantity]);

  const defaultPaymentMethod = useMemo(() => {
    if (!paymentMethods || paymentMethods.count === 0) {
      return null;
    }

    // Find the first default payment method
    let defaultPm = paymentMethods.results.find((pm) => pm.default);
    if (!defaultPm) {
      // If no default payment method, return the first one
      defaultPm = paymentMethods.results[0];
    }

    return defaultPm;

  }, [paymentMethods]);

  // when premiumPlan changes to another premium plan (tier based plan selection), update selected plan if current selected plan is premium 
  useEffect(() => {
    if (selectedPlan?.group === 'premium' && premiumPlan) {
      setSelectedPlan(premiumPlan);
    }

  }, [premiumPlan, selectedPlan]);

  const activatePlan = useCallback(async (ignorePaymentMethodCheck: boolean = false) => {
    if (!selectedPlan) return;
    if (!stripe) return;

    setInitialContext(false);

    // if no payment method is available, show the payment form
    if (!defaultPaymentMethod && !ignorePaymentMethodCheck) {
      setView('payment');
      return;
    }

    setView('pending');
    setApiError(undefined);

    try {
      const createResponse = await activateSubscription({
        billing_period: period,
        products: [
          {
            identifier: selectedPlan.identifier,
            period,
            quantity: planQuantity,
          },
        ]
      }).unwrap();


      const successCallback = async () => {
        setView('success');
        await new Promise((resolve) => setTimeout(resolve, 4000)); // wait for a second before refetching again
        dispatch(authApi.util.invalidateTags(['AuthContext']));
      }

      if (createResponse.status === SubscriptionStatus.INCOMPLETE && createResponse.client_secret) {

        setView('incomplete_pending');
        // if the subscription is incomplete, we need to confirm the payment method
        const clientSecret = createResponse.client_secret;

        // confirm the card payment
        const confirmResponse = await stripe.confirmCardPayment(clientSecret);
        if (confirmResponse.error) {
          if (confirmResponse.error.type === 'card_error' || confirmResponse.error.type === 'validation_error') {
            enqueueSnackbar(confirmResponse.error.message, { variant: 'error' });
          }
          else {
            enqueueSnackbar(getConfirmPaymentErrorMessage(confirmResponse.error), { variant: 'error' });
          }
        }
        else {
          await successCallback();
        }

        dispatch(authApi.util.invalidateTags(['Subscription']));
      }
      else {
        await successCallback();
      }

      setTimeout(() => {
        // redirect to the dashboard after 5 seconds
        router.push(paths.dashboard.root);
      }, 2000);

    } catch (e) {
      console.error(e);

      setView('plans');

      const message = getSubscriptionErrorMessage(e);
      setApiError(message);
    }
  }, [
    activateSubscription,
    defaultPaymentMethod,
    dispatch,
    period,
    planQuantity,
    selectedPlan,
    router,
    stripe,
    enqueueSnackbar
  ]);

  const confirmCardSetup = useCallback(async (clientSecret: string) => {
    if (!stripe) return;

    const url = window.location.href;

    // Confirm the SetupIntent using the details collected by the Payment Element
    try {
      const confirmation = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: url,
        },
      });

      if (confirmation.error) {
        enqueueSnackbar(getIntentErrorMessage(confirmation.error), { variant: 'error' });
        setError(getIntentErrorMessage(confirmation.error));
      } else {
        // customer is redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer is redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.

        await createPaymentMethod({
          payment_method: confirmation.setupIntent.payment_method as string,
        }).unwrap();

        enqueueSnackbar(t('billing.api.card.create.confirm_setup.success'), { variant: 'success' });

        await refetch();

        // continue to plan activation if a plan is selected
        if (selectedPlan) {
          activatePlan(true);
        }
      }
    } catch (e) {
      console.error(e);
      setError(t('billing.api.card.create.intent.default_error'));
      enqueueSnackbar(t('billing.api.card.create.intent.default_error'), { variant: 'error' });
    }

    setLoading(false);
  }, [stripe, elements, enqueueSnackbar, t, createPaymentMethod, refetch, selectedPlan, activatePlan]);

  const handleSubmit = useCallback(async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    setError(null);

    if (!stripe) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      enqueueSnackbar(getIntentErrorMessage(submitError), { variant: 'error' });
      setError(getIntentErrorMessage(submitError));
      setLoading(false);
      return;
    }

    // Create the SetupIntent and obtain clientSecret
    let res;
    try {
      res = await getSetupIntent().unwrap();
    } catch (e) {
      console.error(e);
      setError(getIntentErrorMessage(e));
      enqueueSnackbar(getIntentErrorMessage(e), { variant: 'error' });
      setLoading(false);
      return;
    }

    const { client_secret: clientSecret } = res;

    confirmCardSetup(clientSecret);
  }, [stripe, elements, enqueueSnackbar, getSetupIntent, confirmCardSetup]);

  const renderFeatureList = (features: FeatureDesc[], planType: 'basic' | 'premium') => (
    <List sx={{ py: 0 }}>
      {features.map((feature: FeatureDesc) => {
        const isIncluded = feature.available;

        return (
          <ListItem key={feature.identifier} sx={{ px: 0, py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Iconify
                icon={isIncluded ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-fill'}
                sx={{
                  width: 20,
                  height: 20,
                  color: isIncluded ? 'success.main' : 'text.disabled',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={t(`billing.plan_selector.features_desc.${feature.identifier}`)}
              primaryTypographyProps={{
                variant: 'body2',
                color: isIncluded ? 'text.primary' : 'text.disabled',
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  const renderPlanCard = (plan: Product, planType: 'basic' | 'premium', isPopular = false) => {
    const features = planFeatures[planType];
    const isSelected = selectedPlan?.identifier === plan.identifier;

    return (
      <Card
        onClick={() => switchProduct(plan)}
        sx={{
          p: { xs: 2, md: 3 },
          height: '100%',
          position: 'relative',
          userSelect: 'none',
          cursor: 'pointer',
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          boxShadow: isSelected ? theme.shadows[8] : theme.shadows[1],
          transform: { xs: 'scale(1)', md: isSelected ? 'scale(1.01)' : 'scale(1)' },
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[8],
            transform: { xs: 'scale(1)', md: isSelected ? 'scale(1.05)' : 'scale(1.02)' },
          },
        }}
      >
        <Stack spacing={2} sx={{ height: '100%', py: 2 }}>

          {/* Plan Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
              {planType === 'basic'
                ? t('billing.plan_selector.basic_plan')
                : t('billing.plan_selector.premium_plan')
              }
            </Typography>

            <Stack direction="column" alignItems="center" justifyContent="center">
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {fCurrency(getPlanPrice(plan, period))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {period === PayPeriod.Yearly ? t('billing.plan_selector.per_year') : t('billing.plan_selector.per_month')}
              </Typography>
            </Stack>

          </Box>

          {/* Quantity Selector */}
          {
            plan.quantity_required ? (
              <Stack direction="column" alignItems="center" sx={{ mt: 2 }} spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanQuantity(Math.max(1, planQuantity - 1));
                      switchProduct(plan); // to refresh the plan selection
                    }}
                    disabled={planQuantity <= 1}
                  >
                    <Iconify icon="eva:minus-fill" />
                  </Button>
                  <Typography variant="subtitle2">{planQuantity}</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanQuantity(planQuantity + 1);
                      switchProduct(plan); // to refresh the plan selection
                    }}
                  >
                    <Iconify icon="eva:plus-fill" />
                  </Button>
                </Stack>
                <Typography variant="subtitle2">
                  {t('billing.plan_selector.team_seats')}
                </Typography>
              </Stack>
            )
              :
              (
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', textAlign: 'center' }}>
                  {t('billing.product.team_seat_desc', {
                    count: getIncludedTeamSeats(plan),
                  })}
                </Typography>
              )
          }

          {/* Features List */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('billing.plan_selector.features')}
            </Typography>
            {renderFeatureList(features, planType)}
          </Box>

          {/* Popularity Chip */}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            {isPopular && (
              <Chip
                label={t('billing.plan_selector.most_popular')}
                variant='outlined'
                color="primary"
                icon={<Iconify icon="noto:fire" />}
                sx={{
                  fontWeight: 'bold',
                  width: '100%'
                }}
              />
            )}
          </Stack>

        </Stack>
      </Card>
    );
  };

  if (isSubscriptionLoading || isAvailableProductsLoading || isLoadingCompanyProfile || isPaymentMethodsLoading) {
    return (
      <Container maxWidth="xs" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <>
      {
        view === 'pending' && (
          <Container maxWidth="xs" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
            <Stack sx={{ textAlign: 'center', minHeight: 400, width: '100%' }} alignItems="center" justifyContent="center" >
              <CircularProgress size={64} />
              <Typography variant="h5" sx={{ mt: 2 }}>
                {t('billing.plan_selector.loading.title')}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {t('billing.plan_selector.loading.subtitle')}
              </Typography>
            </Stack>
          </Container>
        )
      }

      {
        view === 'success' && (
          <Container maxWidth="xs" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
            <Stack sx={{ textAlign: 'center', minHeight: 400, width: '100%' }} alignItems="center" justifyContent="center" >
              <Box>
                <AnimationContainer height={250} json={successAnimation} loop={false} />
              </Box>
              <Typography variant="h5" sx={{ mt: 2 }}>
                {t('billing.plan_selector.success.title')}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {t('billing.plan_selector.success.subtitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
                {t('billing.plan_selector.success.redirect')}
              </Typography>
            </Stack>
          </Container>
        )
      }

      {
        view === 'payment' && (
          <Container maxWidth="sm" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
            <Stack spacing={2} sx={{ textAlign: 'center', minHeight: 400 }} alignItems="center" justifyContent="center">
              <Box sx={{ textAlign: 'center' }}>
                <Button variant="text" startIcon={<Iconify icon="eva:arrow-back-fill" />} onClick={() => setView('plans')}>
                  {t('billing.plan_selector.back_to_plans')}
                </Button>
              </Box>

              {
                defaultPaymentMethod ?
                  <Box>
                    <Typography variant="h4">
                      {t('billing.payment.change_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('billing.payment.change_description')}
                    </Typography>
                  </Box>
                  :
                  <Box>
                    <Typography variant="h4">
                      {t('billing.payment.setup_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('billing.payment.setup_description')}
                    </Typography>
                  </Box>
              }

              <Card sx={{ p: 3, width: '100%' }}>
                <Stack spacing={3}>
                  {error && <Alert severity="error">{error}</Alert>}

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <PaymentElement />

                      <LoadingButton
                        fullWidth
                        size="large"
                        variant="contained"
                        type="submit"
                        disabled={!stripe}
                        loading={loading}
                      >
                        {t('billing.payment.add_payment_method')}
                      </LoadingButton>
                    </Stack>
                  </form>
                </Stack>
              </Card>
            </Stack>
          </Container>
        )
      }

      {
        view === 'plans' && (
          <Container maxWidth="md" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {t('billing.plan_selector.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                {t('billing.plan_selector.subtitle')}
              </Typography>
            </Box>

            {/* Error Message */}
            {apiError && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="error.main">
                  {apiError}
                </Typography>
              </Box>
            )}

            {/* Payment Method Alert */}
            {defaultPaymentMethod && (
              <Alert severity="success"
                sx={{ mb: 3 }}
                action={
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setView('payment')}
                    sx={{ ml: 1 }}
                  >
                    {t('billing.payment.change_payment_method')}
                  </Button>
                }
              >
                <Typography variant="subtitle2">
                  {t('billing.payment.payment_method_available')}
                </Typography>
              </Alert>
            )}

            {/* Period Selector */}
            <Stack direction="column" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Stack direction="row" justifyContent="center" spacing={2}>
                <Button
                  variant={period === PayPeriod.Monthly ? 'contained' : 'outlined'}
                  onClick={() => setPeriod(PayPeriod.Monthly)}
                >
                  {t('common.monthly')}
                </Button>
                <Button
                  variant={period === PayPeriod.Yearly ? 'contained' : 'outlined'}
                  onClick={() => setPeriod(PayPeriod.Yearly)}
                >
                  {t('common.yearly')}
                </Button>
              </Stack>
            </Stack>

            {/* Pricing Cards */}
            <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                {basePlan && renderPlanCard(basePlan, 'basic', false)}
              </Grid>
              <Grid item xs={12} md={6}>
                {premiumPlan && renderPlanCard(premiumPlan, 'premium', true)}
              </Grid>
            </Grid>

            {/* Call to Action */}
            <Box sx={{ mt: 5, mb: 2 }}>
              <Button
                size="large"
                variant="contained"
                onClick={() => activatePlan()}
                disabled={!selectedPlan}
                fullWidth
              >
                {t('common.continue')}
              </Button>
            </Box>

            {/* Additional Info */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('billing.plan_selector.additional_info')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                <Trans
                  i18nKey="somelink"
                  components={{
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    anchorLink: <a href="https://share-ap1.hsforms.com/1VTEFbDZsQsycbPEpYKOk_wqgbyd" target="_blank" rel="noreferrer" />,
                  }}
                >
                  {t('billing.plan_selector.request_demo_link')}
                </Trans>
              </Typography>
            </Box>
          </Container>
        )
      }

      {
        view === 'incomplete_pending' && (
          <Container maxWidth="xs" sx={{ py: { xs: 2 }, px: { xs: 5 }, width: '100vw' }}>
            <Stack sx={{ textAlign: 'center', minHeight: 400, width: '100%' }} alignItems="center" justifyContent="center" >
              <Box>
                <CircularProgress size={64} />
              </Box>
              <Typography variant="h5" sx={{ mt: 2 }}>
                {t('billing.plan_selector.incomplete.title')}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {t('billing.plan_selector.incomplete.subtitle')}
              </Typography>
            </Stack>
          </Container>
        )
      }

    </>
  );

}